import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { buildHill } from "@/render/terrain";

export type ViewMode = "perspective" | "elevation";

export interface Viewport {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  /** Register a per-frame callback. dt is seconds since last frame. */
  onFrame: (cb: (dt: number) => void) => void;
  /** Switch between orbit (perspective) and locked side elevation. */
  setView: (mode: ViewMode) => void;
  getView: () => ViewMode;
  getCamera: () => THREE.Camera;
  /** Frame the cameras around a tower of the given height/width (m). */
  frameTower: (height: number, halfWidth: number) => void;
  start: () => void;
}

function makeSkyTexture(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = 2;
  c.height = 256;
  const g = c.getContext("2d")!;
  const grad = g.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0.0, "#7fa9d4"); // zenith
  grad.addColorStop(0.55, "#a9c6e0");
  grad.addColorStop(1.0, "#d9e4ec"); // horizon haze
  g.fillStyle = grad;
  g.fillRect(0, 0, 2, 256);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function createViewport(canvas: HTMLCanvasElement): Viewport {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  const horizon = new THREE.Color(0xd9e4ec);
  scene.background = makeSkyTexture();
  scene.fog = new THREE.Fog(horizon, 130, 340);

  // Neutral studio environment → gives the metal something to reflect, which is
  // what stops it looking flat. Cheap, generated once.
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  // --- cameras ---
  const persp = new THREE.PerspectiveCamera(50, 1, 0.1, 2000);
  persp.position.set(26, 20, 32);

  const ortho = new THREE.OrthographicCamera(-20, 20, 40, 0, 0.1, 2000);
  ortho.position.set(0, 20, 260);

  let active: THREE.Camera = persp;
  let mode: ViewMode = "perspective";

  const controls = new OrbitControls(persp, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.target.set(0, 10, 0);
  controls.maxPolarAngle = Math.PI * 0.495;
  controls.minDistance = 6;
  controls.maxDistance = 220;
  // middle-mouse orbits (as well as left); right-drag pans
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.ROTATE,
    RIGHT: THREE.MOUSE.PAN,
  };

  // --- lighting: warm key sun + cool sky fill + cool back-rim ---
  const sun = new THREE.DirectionalLight(0xfff2d6, 3.1);
  sun.position.set(42, 60, 28);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 240;
  sun.shadow.bias = -0.0004;
  const s = 60;
  sun.shadow.camera.left = -s;
  sun.shadow.camera.right = s;
  sun.shadow.camera.top = s;
  sun.shadow.camera.bottom = -s;
  scene.add(sun);

  const hemi = new THREE.HemisphereLight(0xbcd6f0, 0x4a4436, 0.55);
  scene.add(hemi);

  const rim = new THREE.DirectionalLight(0xbfd4ff, 0.7);
  rim.position.set(-38, 26, -30);
  scene.add(rim);

  // --- terrain ---
  scene.add(buildHill());

  // --- resize & framing ---
  let elevHeight = 40;
  let elevHalf = 24;

  function applyOrthoFrustum(aspect: number) {
    const halfH = elevHeight * 0.62;
    const halfW = Math.max(elevHalf * 1.6, halfH * aspect);
    ortho.left = -halfW;
    ortho.right = halfW;
    ortho.top = elevHeight * 1.02;
    ortho.bottom = -6;
    // keep vertical framing regardless of aspect
    ortho.left = -Math.max(elevHalf * 1.6, (elevHeight * 1.08 * aspect) / 2);
    ortho.right = -ortho.left;
    ortho.updateProjectionMatrix();
  }

  function resize() {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== w || canvas.height !== h) {
      renderer.setSize(w, h, false);
      const aspect = w / h;
      persp.aspect = aspect;
      persp.updateProjectionMatrix();
      applyOrthoFrustum(aspect);
    }
  }
  window.addEventListener("resize", resize);

  function frameTower(height: number, halfWidth: number) {
    elevHeight = height;
    elevHalf = halfWidth;
    controls.target.set(0, height * 0.42, 0);
    const d = height * 1.4 + 14;
    // always let the user zoom out far enough to see the whole tower (tall masts
    // + wide guy anchor spreads), regardless of size
    controls.maxDistance = Math.max(300, height * 6, halfWidth * 8, d * 3);
    // push the fog back so zooming out doesn't fade the tower's colour to the sky
    const fog = scene.fog as THREE.Fog | null;
    if (fog) {
      fog.near = Math.max(130, d);
      fog.far = Math.max(340, height * 14, halfWidth * 12, d * 8);
    }
    persp.position.set(d * 0.7, height * 0.65, d * 0.75);
    ortho.position.set(0, height * 0.5, 300);
    ortho.lookAt(0, height * 0.5, 0);
    applyOrthoFrustum(canvas.clientWidth / Math.max(1, canvas.clientHeight));
    controls.update();
  }

  function setView(next: ViewMode) {
    mode = next;
    if (next === "elevation") {
      active = ortho;
      controls.enabled = false;
      ortho.lookAt(0, elevHeight * 0.5, 0);
    } else {
      active = persp;
      controls.enabled = true;
    }
  }

  const callbacks: Array<(dt: number) => void> = [];
  const clock = new THREE.Clock();

  function loop() {
    requestAnimationFrame(loop);
    resize();
    const dt = Math.min(clock.getDelta(), 0.05);
    for (const cb of callbacks) cb(dt);
    if (mode === "perspective") controls.update();
    renderer.render(scene, active);
  }

  return {
    scene,
    renderer,
    controls,
    onFrame: (cb) => callbacks.push(cb),
    setView,
    getView: () => mode,
    getCamera: () => active,
    frameTower,
    start: () => {
      resize();
      loop();
    },
  };
}
